import { useState } from "react"
import { Button } from "@/components/ui/button"
import { ChevronDown, Check, Minus, Plus, Calendar, Clock, MapPin, User, FileText } from "lucide-react"

interface BookingData {
  location: string
  serviceType: string
  frequencyOption: string
  frequencyPricePerHour: number
  propertyTier: string
  propertyPrice: number
  regularRoomOption: string
  regularRoomPrice: number
  bookingHours: number 
  additionalRooms: string[]
  preferredDate: string
  preferredTime: string
  accessMethod: string
  keyLocationAddress: string
  clientPresent: string 
  fullName: string
  email: string
  phone: string
  address: string
  agreeTerms: boolean
  agreePrivacy: boolean
}

export default function Component() {
  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [bookingReference, setBookingReference] = useState("")
  
  const [bookingData, setBookingData] = useState<BookingData>({
    location: "",
    serviceType: "",
    frequencyOption: "once-weekly", 
    frequencyPricePerHour: 23,     
    propertyTier: "",
    propertyPrice: 0,
    regularRoomOption: "",
    regularRoomPrice: 0,
    bookingHours: 2,               
    additionalRooms: [],
    preferredDate: "",
    preferredTime: "",
    accessMethod: "",
    keyLocationAddress: "",
    clientPresent: "",
    fullName: "",
    email: "",
    phone: "",
    address: "",
    agreeTerms: false,
    agreePrivacy: false,
  })

  const isRegularClean = bookingData.serviceType === "Regular/Domestic Cleaning"

  const stepsList = isRegularClean
    ? [
        { number: 1, label: "Service" },
        { number: 2, label: "Frequency" },
        { number: 3, label: "Property & Hours" },
        { number: 4, label: "Schedule" },
        { number: 5, label: "Contact" },
        { number: 6, label: "Review" },
      ]
    : [
        { number: 1, label: "Service" },
        { number: 2, label: "Property" },
        { number: 3, label: "Schedule" },
        { number: 4, label: "Contact" },
        { number: 5, label: "Review" },
      ]

  const scheduleStepIndex = isRegularClean ? 4 : 3
  const contactStepIndex = isRegularClean ? 5 : 4
  const reviewStepIndex = isRegularClean ? 6 : 5

  const serviceOptions = [
    "End Of Tenancy Cleaning",
    "Deep Cleaning",
    "⁠Office/Commercial Cleaning",
    "⁠One-Off Cleaning",
    "Regular/Domestic Cleaning",
  ]

  const locationsList = ["Chelmsford", "Colchester", "Ipswich", "Harwich", "Braintree", "Cambridge", "Clacton-on-Sea"]

  const frequencyOptionsList = [
    { id: "once-weekly", title: "Once Weekly", desc: "Perfect for maintaining a consistently clean home", rate: 23, badge: "Popular", badgeClass: "bg-blue-50 text-blue-600 border-blue-100" },
    { id: "twice-weekly", title: "Twice Weekly", desc: "Ideal for busy households needing regular upkeep", rate: 23, badge: "Popular", badgeClass: "bg-blue-50 text-blue-600 border-blue-100" },
    { id: "more-twice-weekly", title: "More Than Twice Weekly", desc: "Best value for frequent cleaning needs", rate: 21, badge: "Best Value", badgeClass: "bg-emerald-50 text-emerald-600 border-emerald-100" },
    { id: "once-2-weeks", title: "Once Every 2 Weeks", desc: "Great for lighter cleaning requirements", rate: 25, badge: "Flexible", badgeClass: "bg-amber-50 text-amber-600 border-amber-100" },
  ]

  const complexPropertyTiers = [
  { id: "studio", name: "Studio Flat", price: 115 },
  { id: "1b1b", name: "1 Bedroom, 1 Bathroom, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 150 },
  { id: "2b1b", name: "2 Bedrooms, 1 Bathroom, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 195 },
  { id: "2b2b", name: "2 Bedrooms, 2 Bathrooms, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 215 },
  { id: "3b1b", name: "3 Bedrooms, 1 Bathroom, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 230 },
  { id: "3b2b", name: "3 Bedrooms, 2 Bathrooms, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 240 },
  { id: "3b3b", name: "3 Bedrooms, 3 Bathrooms, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 260 },
  { id: "4b1b", name: "4 Bedrooms, 1 Bathroom, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 280 },
  { id: "4b2b", name: "4 Bedrooms, 2 Bathrooms, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 310 },
  { id: "4b3b", name: "4 Bedrooms, 3 Bathrooms, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 325 },
  { id: "4b4b", name: "4 Bedrooms, 4 Bathrooms, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 330 },
  { id: "5b2b", name: "5 Bedrooms, 2 Bathrooms, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 340 },
  { id: "5b3b", name: "5 Bedrooms, 3 Bathrooms, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 355 },
  { id: "5b4b", name: "5 Bedrooms, 4 Bathrooms, Hallway, Living Area/Lounge, Kitchen, and all Appliances", price: 365 }
];

  const regularRoomOptions = [
    { id: "1r", name: "1 Bedroom Apartment", price: 90 },
    { id: "2r", name: "2 Bedrooms Apartment", price: 130 },
    { id: "3r", name: "3 Bedrooms Apartment", price: 170 },
    { id: "4r", name: "4 Bedrooms Apartment", price: 250 },
    { id: "5r", name: "5 Bedrooms Apartment", price: 300 },
  ]

  const addOnRoomsList = [
    "Studio", "En-suite", "Cloak", "Office", 
    "Conservatory", "Garage", "Dining Room", 
    "Dressing Room", "Utility Room", "Balcony"
  ]

  const generateTimeSlots = () => {
    const slots = []
    for (let hour = 8; hour <= 17; hour++) {
      const formattedHour = hour.toString().padStart(2, "0")
      slots.push(`${formattedHour}:00`)
      if (hour !== 17) slots.push(`${formattedHour}:30`)
    }
    return slots
  }

  const hourlyRateTotal = isRegularClean ? bookingData.bookingHours * bookingData.frequencyPricePerHour : 0
  const propertyBaseTotal = isRegularClean ? bookingData.regularRoomPrice : bookingData.propertyPrice
  const addOnsTotal = isRegularClean ? 0 : bookingData.additionalRooms.length * 10
  const orderTotal = hourlyRateTotal + propertyBaseTotal + addOnsTotal

  const handleToggleAddOn = (roomName: string) => {
    setBookingData(prev => {
      const alreadySelected = prev.additionalRooms.includes(roomName)
      return {
        ...prev,
        additionalRooms: alreadySelected
          ? prev.additionalRooms.filter(r => r !== roomName)
          : [...prev.additionalRooms, roomName]
      }
    })
  }

  const handleLocationChange = (selectedLocation: string) => {
    setBookingData(prev => {
      const needsServiceReset = selectedLocation !== "Colchester" && prev.serviceType === "Regular/Domestic Cleaning"
      return {
        ...prev,
        location: selectedLocation,
        serviceType: needsServiceReset ? "" : prev.serviceType,
        frequencyOption: "once-weekly",
        frequencyPricePerHour: 23,
        propertyTier: "",
        propertyPrice: 0,
        regularRoomOption: "",
        regularRoomPrice: 0,
        additionalRooms: [],
        bookingHours: 2
      }
    })
  }

  const handleHourStepper = (direction: "inc" | "dec") => {
    setBookingData(prev => {
      const currentHours = prev.bookingHours
      if (direction === "dec" && currentHours <= 2) return prev 
      return {
        ...prev,
        bookingHours: direction === "inc" ? currentHours + 1 : currentHours - 1
      }
    })
  }

 const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, "");
    const timeStr = now.toTimeString().slice(0, 5).replace(/:/g, "");
    // Matches the reference format: EPC-YYYYMMDD-HHMM
    const referenceCode = `EPC-${dateStr}-${timeStr}`;
    
    setBookingReference(referenceCode);
try {
  // Connects directly to your live production Render service instance
  await fetch("https://ecopristine-booking-api.onrender.com/api/bookings", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      reference: referenceCode,
      orderTotal: orderTotal,
      fullName: bookingData.fullName,
      email: bookingData.email,
      phone: bookingData.phone,
      serviceType: bookingData.serviceType,
      preferredDate: bookingData.preferredDate,
      preferredTime: bookingData.preferredTime,
      address: bookingData.address
    }),
  });
      
      setIsConfirmed(true);
    } catch (e) {
      console.error("Submission network error:", e);
      setIsConfirmed(true); // Fallback to keep the UI transition smooth for the user
    } finally {
      setIsSubmitting(false);
    }
  };
  const isScheduleValid = 
    bookingData.preferredDate !== "" && 
    bookingData.preferredTime !== "" && 
    bookingData.clientPresent !== "" &&
    (bookingData.accessMethod === "property" || (bookingData.accessMethod === "different" && bookingData.keyLocationAddress.trim() !== ""))

  const isContactValid = 
    bookingData.fullName.trim() !== "" &&
    bookingData.email.trim() !== "" &&
    bookingData.phone.trim() !== "" &&
    bookingData.address.trim() !== "" &&
    bookingData.agreeTerms &&
    bookingData.agreePrivacy

  const getPropertyLabel = () => {
    if (isRegularClean) {
      return regularRoomOptions.find(o => o.id === bookingData.regularRoomOption)?.name || "—"
    }
    return complexPropertyTiers.find(t => t.id === bookingData.propertyTier)?.name || "—"
  }

  const availableServices = serviceOptions.filter(service => {
    if (service === "Regular/Domestic Cleaning") return bookingData.location === "Colchester"
    return true
  })

  return (
    <div className="w-full min-h-screen bg-[#f8fafc] py-10 px-4 flex flex-col items-center">
      
      {/* ==========================================================
         A4 DYNAMIC PRINT RECEIPT LAYOUT
         (Hidden on screen, renders cleanly on window.print Call)
         ========================================================== */}
      <div className="hidden print:block w-full max-w-3xl p-10 bg-white text-slate-800 font-sans text-sm leading-relaxed mx-auto">
        <div className="flex justify-between items-start border-b-2 border-slate-200 pb-6 mb-8">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900 uppercase">Eco Pristine Cleaning Co. Ltd.</h1>
            <p className="text-xs text-slate-500 mt-1">Professional Hygiene & Property Solutions</p>
          </div>
          <div className="text-right">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block">Booking Reference</span>
            <div className="text-xl font-mono font-bold text-[#0066cc]">{bookingReference}</div>
            <p className="text-xs text-slate-500 mt-1">Date Issued: {new Date().toLocaleDateString('en-GB')}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-8 bg-slate-50 p-5 rounded-xl border border-slate-100">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Customer Details</h3>
            <p className="font-bold text-slate-900">{bookingData.fullName || "Sam"}</p>
            <p className="text-slate-600 font-medium">{bookingData.email || "niniolasamuel@gmail.com"}</p>
            <p className="text-slate-600">{bookingData.phone || "07700 00000"}</p>
            <p className="text-slate-500 text-xs mt-2 max-w-xs">{bookingData.address}</p>
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Schedule Details</h3>
            <p className="font-medium text-slate-800"><strong>Date:</strong> {bookingData.preferredDate || "16 June 2026"}</p>
            <p className="font-medium text-slate-800"><strong>Time:</strong> {bookingData.preferredTime || "9:00AM"}</p>
            <p className="text-slate-600 capitalize mt-1"><strong>Access:</strong> {bookingData.accessMethod === "property" ? "Keys at Property" : "Key Exchange Address Provided"}</p>
          </div>
        </div>

        <div className="mb-8">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Pricing Matrix</h3>
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 text-slate-400 text-xs uppercase font-bold">
                <th className="pb-2 font-semibold">Service Details & Description</th>
                <th className="pb-2 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
              <tr>
                <td className="py-3.5">
                  <div className="font-bold text-slate-900">{bookingData.serviceType || "End Of Cleaning Service"}</div>
                  <div className="text-xs text-slate-400 font-normal mt-0.5">Base Structure Unit: {getPropertyLabel()}</div>
                </td>
                <td className="py-3.5 text-right font-bold text-slate-900">£{propertyBaseTotal}</td>
              </tr>
              {isRegularClean && (
                <tr>
                  <td className="py-3.5 text-slate-600">Maintenance Schedule Operations Time Allocation ({bookingData.bookingHours} hrs @ £{bookingData.frequencyPricePerHour}/hr)</td>
                  <td className="py-3.5 text-right font-bold text-slate-900">£{hourlyRateTotal}</td>
                </tr>
              )}
              {!isRegularClean && bookingData.additionalRooms.length > 0 && (
                <tr>
                  <td className="py-3.5 text-slate-600">Add-On(s) Module Expansion Array ({bookingData.additionalRooms.join(", ")})</td>
                  <td className="py-3.5 text-right font-bold text-slate-900">£{addOnsTotal}</td>
                </tr>
              )}
              <tr className="border-t-2 border-slate-200 text-base">
                <td className="py-4 font-black text-slate-900">Total Amount Payable</td>
                <td className="py-4 text-right font-black text-[#0066cc] text-xl">£{orderTotal}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-slate-950 text-white rounded-xl p-5 space-y-3 mt-12">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 tracking-wide">Bank Details (Official Remittance Node)</h3>
          <div className="grid grid-cols-3 gap-4 text-xs font-mono">
            <div><span className="text-slate-400 block font-sans mb-0.5">Account Name</span><strong>ECO PRISTINE CLEANING CO LTD</strong></div>
            <div><span className="text-slate-400 block font-sans mb-0.5">Sort Code</span><strong>40-46-09</strong></div>
            <div><span className="text-slate-400 block font-sans mb-0.5">Account Number</span><strong>02206331</strong></div>
          </div>
        </div>
      </div>

      {/* ==========================================================
         SCREEN WORKSPACE PANELS (Hidden dynamically during print)
         ========================================================== */}
      <div className="print:hidden w-full max-w-4xl flex flex-col items-center">
        
        {/* Progress Timeline Header Header */}
        <div className="w-full mb-12 relative px-4">
          <div className="absolute top-5 left-[calc(100%/${stepsList.length}*0.5+1rem)] right-[calc(100%/${stepsList.length}*0.5+1rem)] h-1 -z-10">
            <div className="absolute inset-0 bg-slate-200 rounded-full" />
            <div 
              className="absolute left-0 top-0 h-full bg-[#0066cc] transition-all duration-500 ease-in-out rounded-full" 
              style={{ width: `${((currentStep - 1) / (stepsList.length - 1)) * 100}%` }} 
            />
          </div>

          <div className="w-full flex items-center justify-between">
            {stepsList.map((st) => (
              <div key={st.number} className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                    currentStep === st.number
                      ? "bg-[#0066cc] text-white ring-4 ring-blue-100"
                      : currentStep > st.number
                      ? "bg-[#0066cc] text-white"
                      : "bg-[#e2e8f0] text-slate-500"
                  }`}
                >
                  {currentStep > st.number ? <Check className="h-4 w-4 stroke-[3]" /> : st.number}
                </div>
                <span className={`text-xs mt-2 font-medium tracking-wide ${currentStep === st.number ? "text-[#0066cc] font-semibold" : "text-slate-500"}`}>
                  {st.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {!isConfirmed ? (
          <div className="w-full max-w-5xl flex flex-col lg:flex-row gap-8 items-start justify-center">
            
            {/* Form inputs Panel */}
            <div className="w-full lg:flex-1 bg-white rounded-xl shadow-sm border border-slate-100 p-8 md:p-12">
              
              {/* STEP 1 */}
              {currentStep === 1 && (
                <div className="space-y-8">
                  <div className="space-y-2 text-left">
                    <h1 className="text-3xl font-bold text-[#0d233a] tracking-tight">Book Your Cleaning Service</h1>
                    <p className="text-slate-500 text-base">Let's start with your location and service type</p>
                  </div>

                  <div className="space-y-2 text-left">
                    <label className="block text-sm font-semibold text-slate-700">Service Location <span className="text-rose-500">*</span></label>
                    <div className="relative">
                      <select
                        value={bookingData.location}
                        onChange={(e) => handleLocationChange(e.target.value)}
                        className="w-full h-14 px-4 bg-white border border-slate-200 rounded-lg text-slate-700 font-medium appearance-none focus:outline-none focus:border-blue-500 transition-all cursor-pointer"
                      >
                        <option value="">Select your location</option>
                        {locationsList.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
                      </select>
                      <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-slate-400"><ChevronDown className="h-5 w-5" /></div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-2 text-left">
                    <label className="block text-sm font-semibold text-slate-700">Select the type of service you require <span className="text-rose-500">*</span></label>
                    <div className="space-y-3">
                      {availableServices.map((service) => (
                        <label key={service} className={`flex items-center p-5 rounded-xl border transition-all duration-200 cursor-pointer hover:bg-slate-50/50 ${bookingData.serviceType === service ? "border-blue-500 bg-blue-50/10 shadow-sm" : "border-slate-200"}`}>
                          <div className="relative flex items-center justify-center mr-4">
                            <input type="radio" name="serviceType" checked={bookingData.serviceType === service} onChange={() => setBookingData({ ...bookingData, serviceType: service, propertyTier: "", propertyPrice: 0, regularRoomOption: "", regularRoomPrice: 0, additionalRooms: [] })} className="sr-only" />
                            <div className={`w-5 h-5 rounded-full border transition-all flex items-center justify-center ${bookingData.serviceType === service ? "border-[#0066cc] bg-white" : "border-slate-300 bg-white"}`}>
                              {bookingData.serviceType === service && <div className="w-2.5 h-2.5 rounded-full bg-[#0066cc]" />}
                            </div>
                          </div>
                          <span className="text-slate-800 font-medium text-sm md:text-base">{service}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button disabled={!bookingData.location || !bookingData.serviceType} onClick={() => setCurrentStep(2)} className="w-full h-12 text-sm font-semibold text-white bg-[#0066cc] hover:bg-[#0052a3] rounded-lg shadow-sm tracking-wide">
                      Continue
                    </Button>
                  </div>
                </div>
              )}

              {/* STEP 2: FREQUENCY (Colchester Regular only) */}
              {currentStep === 2 && isRegularClean && (
                <div className="space-y-8">
                  <div className="space-y-2 text-left">
                    <h1 className="text-3xl font-bold text-[#0d233a] tracking-tight">How often would you like cleaning?</h1>
                    <p className="text-slate-500 text-base">Choose your preferred frequency metrics</p>
                  </div>

                  <div className="space-y-4 text-left">
                    {frequencyOptionsList.map((option) => (
                      <label key={option.id} className={`flex relative items-start p-6 rounded-xl border transition-all duration-200 cursor-pointer hover:bg-slate-50/50 ${bookingData.frequencyOption === option.id ? "border-blue-500 bg-blue-50/5 shadow-sm ring-1 ring-blue-500" : "border-slate-200"}`}>
                        <div className="relative flex items-center justify-center mr-4 mt-1">
                          <input type="radio" name="frequencyOption" checked={bookingData.frequencyOption === option.id} onChange={() => setBookingData({ ...bookingData, frequencyOption: option.id, frequencyPricePerHour: option.rate })} className="sr-only" />
                          <div className={`w-5 h-5 rounded-full border transition-all flex items-center justify-center ${bookingData.frequencyOption === option.id ? "border-[#0066cc] bg-white" : "border-slate-300 bg-white"}`}>
                            {bookingData.frequencyOption === option.id && <div className="w-2.5 h-2.5 rounded-full bg-[#0066cc]" />}
                          </div>
                        </div>
                        
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-800 font-bold text-lg">{option.title}</span>
                            <span className={`text-xs px-2.5 py-1 font-semibold rounded-full border ${option.badgeClass}`}>{option.badge}</span>
                          </div>
                          <p className="text-slate-500 text-sm">{option.desc}</p>
                          <div className="pt-2">
                            <span className="text-xl font-bold text-[#0066cc]">£{option.rate}</span>
                            <span className="text-slate-500 text-sm font-medium"> per hour per cleaner</span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="flex gap-4 pt-6 border-t border-slate-100">
                    <Button variant="outline" onClick={() => setCurrentStep(1)} className="h-12 px-6 rounded-lg font-medium text-slate-700">← Back</Button>
                    <Button disabled={!bookingData.frequencyOption} onClick={() => setCurrentStep(3)} className="flex-1 h-12 text-sm font-semibold text-white bg-[#0066cc]">Continue</Button>
                  </div>
                </div>
              )}

              {/* STEP 3: PROPERTY DETAILS */}
              {((currentStep === 2 && !isRegularClean) || (currentStep === 3 && isRegularClean)) && (
                <div className="space-y-8">
                  <div className="space-y-2 text-left">
                    <h1 className="text-3xl font-bold text-[#0d233a] tracking-tight">Tell us about your property</h1>
                    <p className="text-slate-500 text-base">Select your size specifications and timeline criteria below.</p>
                  </div>

                  <div className="space-y-4 text-left">
                    <label className="block text-sm font-semibold text-slate-700">Property Size <span className="text-rose-500">*</span></label>
                    
                    <div className="space-y-3">
                      {!isRegularClean && complexPropertyTiers.map((tier) => (
                        <label key={tier.id} className={`flex items-center justify-between p-5 rounded-xl border transition-all duration-200 cursor-pointer hover:bg-slate-50/50 ${bookingData.propertyTier === tier.id ? "border-blue-500 bg-blue-50/5 shadow-sm ring-1 ring-blue-500" : "border-slate-200"}`}>
                          <div className="flex items-center pr-4">
                            <div className="relative flex items-center justify-center mr-4 shrink-0">
                              <input type="radio" name="propertyTier" checked={bookingData.propertyTier === tier.id} onChange={() => setBookingData({ ...bookingData, propertyTier: tier.id, propertyPrice: tier.price })} className="sr-only" />
                              <div className={`w-5 h-5 rounded-full border transition-all flex items-center justify-center ${bookingData.propertyTier === tier.id ? "border-[#0066cc] bg-white" : "border-slate-300 bg-white"}`}>
                                {bookingData.propertyTier === tier.id && <div className="w-2.5 h-2.5 rounded-full bg-[#0066cc]" />}
                              </div>
                            </div>
                            <span className="text-slate-800 font-medium text-sm leading-relaxed max-w-md">{tier.name}</span>
                          </div>
                          <span className="text-[#0066cc] font-bold shrink-0">£{tier.price}</span>
                        </label>
                      ))}

                      {isRegularClean && regularRoomOptions.map((opt) => (
                        <label key={opt.id} className={`flex items-center justify-between p-5 rounded-xl border transition-all duration-200 cursor-pointer hover:bg-slate-50/50 ${bookingData.regularRoomOption === opt.id ? "border-blue-500 bg-blue-50/5 shadow-sm ring-1 ring-blue-500" : "border-slate-200"}`}>
                          <div className="flex items-center">
                            <div className="relative flex items-center justify-center mr-4">
                              <input type="radio" name="regularRoomOption" checked={bookingData.regularRoomOption === opt.id} onChange={() => setBookingData({ ...bookingData, regularRoomOption: opt.id, regularRoomPrice: opt.price })} className="sr-only" />
                              <div className={`w-5 h-5 rounded-full border transition-all flex items-center justify-center ${bookingData.regularRoomOption === opt.id ? "border-[#0066cc] bg-white" : "border-slate-300 bg-white"}`}>
                                {bookingData.regularRoomOption === opt.id && <div className="w-2.5 h-2.5 rounded-full bg-[#0066cc]" />}
                              </div>
                            </div>
                            <span className="text-slate-800 font-medium text-sm md:text-base">{opt.name}</span>
                          </div>
                          <span className="text-[#0066cc] font-bold shrink-0">£{opt.price}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {isRegularClean && (
                    <div className="p-5 border border-slate-200 rounded-xl bg-slate-50/50 space-y-3 text-left">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-slate-800">Booking Duration</label>
                          <p className="text-xs text-slate-500 mt-0.5">Please note: We require a minimum booking time of 2 hours.</p>
                        </div>
                        
                        <div className="flex items-center gap-3 bg-white p-1.5 border border-slate-200 rounded-lg shadow-sm w-max ml-auto sm:ml-0">
                          <button type="button" onClick={() => handleHourStepper("dec")} disabled={bookingData.bookingHours <= 2} className="w-9 h-9 flex items-center justify-center rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-40 font-bold transition">
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-16 text-center font-bold text-slate-800 text-lg">{bookingData.bookingHours} hrs</span>
                          <button type="button" onClick={() => handleHourStepper("inc")} className="w-9 h-9 flex items-center justify-center rounded bg-slate-100 hover:bg-slate-200 font-bold transition">
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isRegularClean && (
                    <div className="space-y-4 pt-4 border-t border-slate-100 text-left">
                      <h3 className="text-lg font-bold text-[#0d233a]">Add-on additional rooms</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {addOnRoomsList.map((room) => {
                          const isChecked = bookingData.additionalRooms.includes(room)
                          return (
                            <label key={room} className={`flex items-start p-4 rounded-xl border cursor-pointer transition-all ${isChecked ? "border-slate-300 bg-slate-50/50" : "border-slate-200 hover:bg-slate-50/50"}`}>
                              <input type="checkbox" checked={isChecked} onChange={() => handleToggleAddOn(room)} className="rounded border-slate-300 text-[#0066cc] mt-0.5 mr-3 h-4 w-4" />
                              <div className="flex flex-col text-left">
                                <span className="text-sm font-medium text-slate-800">{room}</span>
                                <span className="text-[#0066cc] text-xs font-bold mt-0.5">+£10</span>
                              </div>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4 pt-6 border-t border-slate-100">
                    <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)} className="h-12 px-6 rounded-lg font-medium text-slate-700">← Back</Button>
                    <Button disabled={isRegularClean ? !bookingData.regularRoomOption : !bookingData.propertyTier} onClick={() => setCurrentStep(currentStep + 1)} className="flex-1 h-12 text-sm font-semibold text-white bg-[#0066cc]">Continue</Button>
                  </div>
                </div>
              )}

              {/* STEP 4: SCHEDULE */}
              {currentStep === scheduleStepIndex && (
                <div className="space-y-8">
                  <div className="space-y-2 text-left">
                    <h1 className="text-3xl font-bold text-[#0d233a] tracking-tight">Schedule Your Service</h1>
                    <p className="text-slate-500 text-base">Choose your preferred date and arrival window</p>
                  </div>

                  <div className="space-y-2 text-left">
                    <label className="block text-sm font-semibold text-slate-700">What is your preferred date? *</label>
                    <input type="date" value={bookingData.preferredDate} onChange={(e) => setBookingData({ ...bookingData, preferredDate: e.target.value })} className="w-full h-14 px-4 border border-slate-200 rounded-lg text-slate-700 font-medium" />
                  </div>

                  <div className="space-y-2 pt-2 text-left">
                    <label className="block text-sm font-semibold text-slate-700">What is your preferred starting time? *</label>
                    <div className="relative">
                      <select value={bookingData.preferredTime} onChange={(e) => setBookingData({ ...bookingData, preferredTime: e.target.value })} className="w-full h-14 px-4 bg-white border border-slate-200 rounded-lg text-slate-700 font-medium appearance-none">
                        <option value="">Select a time</option>
                        {generateTimeSlots().map((slot) => <option key={slot} value={slot}>{slot}</option>)}
                      </select>
                      <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-slate-400"><ChevronDown className="h-5 w-5" /></div>
                    </div>
                  </div>

                  <div className="space-y-4 pt-2 text-left">
                    <label className="block text-sm font-semibold text-slate-700">How do we access the property? *</label>
                    <div className="space-y-3">
                      <label className={`flex items-center p-5 rounded-xl border transition-all duration-200 cursor-pointer ${bookingData.accessMethod === "property" ? "border-blue-500 bg-blue-50/10" : "border-slate-200"}`}>
                        <input type="radio" name="accessMethod" checked={bookingData.accessMethod === "property"} onChange={() => setBookingData({ ...bookingData, accessMethod: "property", keyLocationAddress: "" })} className="sr-only" />
                        <div className="w-5 h-5 rounded-full border mr-4 flex items-center justify-center"><div className={`w-2.5 h-2.5 rounded-full ${bookingData.accessMethod === "property" ? "bg-[#0066cc]" : "bg-transparent"}`} /></div>
                        <span className="text-slate-800 font-medium">Keys at the property</span>
                      </label>

                      <label className={`flex items-center p-5 rounded-xl border transition-all duration-200 cursor-pointer ${bookingData.accessMethod === "different" ? "border-blue-500 bg-blue-50/10" : "border-slate-200"}`}>
                        <input type="radio" name="accessMethod" checked={bookingData.accessMethod === "different"} onChange={() => setBookingData({ ...bookingData, accessMethod: "different" })} className="sr-only" />
                        <div className="w-5 h-5 rounded-full border mr-4 flex items-center justify-center"><div className={`w-2.5 h-2.5 rounded-full ${bookingData.accessMethod === "different" ? "bg-[#0066cc]" : "bg-transparent"}`} /></div>
                        <span className="text-slate-800 font-medium">Keys at a different location</span>
                      </label>
                    </div>
                  </div>

                  {bookingData.accessMethod === "different" && (
                    <textarea rows={3} placeholder="Kindly provide the address of where the keys are:" value={bookingData.keyLocationAddress} onChange={(e) => setBookingData({ ...bookingData, keyLocationAddress: e.target.value })} className="w-full p-4 border border-slate-200 rounded-lg text-slate-700 font-medium resize-none" />
                  )}

                  <div className="space-y-4 pt-2 text-left">
                    <label className="block text-sm font-semibold text-slate-700">Clients available at the residence during cleaning *</label>
                    <div className="space-y-3">
                      {["yes", "no"].map(v => (
                        <label key={v} className={`flex items-center p-5 rounded-xl border transition-all duration-200 cursor-pointer ${bookingData.clientPresent === v ? "border-blue-500 bg-blue-50/10" : "border-slate-200"}`}>
                          <input type="radio" name="clientPresent" checked={bookingData.clientPresent === v} onChange={() => setBookingData({ ...bookingData, clientPresent: v })} className="sr-only" />
                          <div className="w-5 h-5 rounded-full border mr-4 flex items-center justify-center"><div className={`w-2.5 h-2.5 rounded-full ${bookingData.clientPresent === v ? "bg-[#0066cc]" : "bg-transparent"}`} /></div>
                          <span className="text-slate-800 font-medium capitalize">{v}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4 pt-6 border-t border-slate-100">
                    <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)} className="h-12 px-6 rounded-lg font-medium text-slate-700">← Back</Button>
                    <Button disabled={!isScheduleValid} onClick={() => setCurrentStep(currentStep + 1)} className="flex-1 h-12 text-sm font-semibold text-white bg-[#0066cc]">Continue</Button>
                  </div>
                </div>
              )}

              {/* STEP 5: CONTACT */}
              {currentStep === contactStepIndex && (
                <div className="space-y-6 text-left">
                  <div className="space-y-2">
                    <h1 className="text-3xl font-bold text-[#0d233a] tracking-tight">Contact Information</h1>
                    <p className="text-slate-500 text-base">We'll use this information to confirm your booking</p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Full Name *</label>
                    <input type="text" placeholder="John Smith" value={bookingData.fullName} onChange={(e) => setBookingData({ ...bookingData, fullName: e.target.value })} className="w-full h-14 px-4 border border-slate-200 rounded-lg text-slate-700 font-medium" />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Email Address *</label>
                    <input type="email" placeholder="john@example.com" value={bookingData.email} onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })} className="w-full h-14 px-4 border border-slate-200 rounded-lg text-slate-700 font-medium" />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Phone Number *</label>
                    <input type="tel" placeholder="07700 900000" value={bookingData.phone} onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })} className="w-full h-14 px-4 border border-slate-200 rounded-lg text-slate-700 font-medium" />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-slate-700">Property Address (including postcode) *</label>
                    <textarea rows={4} placeholder="123 Main Street, Essex" value={bookingData.address} onChange={(e) => setBookingData({ ...bookingData, address: e.target.value })} className="w-full p-4 border border-slate-200 rounded-lg text-slate-700 font-medium resize-none" />
                  </div>

                  <div className="p-6 bg-[#f8fafc] rounded-xl border border-slate-100 space-y-4">
                    <h3 className="text-xl font-bold text-[#0d233a] mb-1">Legal Agreement</h3>
                    <label className="flex items-start cursor-pointer select-none">
                      <input type="checkbox" checked={bookingData.agreeTerms} onChange={(e) => setBookingData({ ...bookingData, agreeTerms: e.target.checked })} className="rounded border-slate-300 text-[#0066cc] mt-1 mr-3 h-4 w-4 shrink-0" />
                      <span className="text-sm text-slate-600 font-medium">I agree to the <span className="text-blue-600 hover:underline">Terms & Conditions</span> *</span>
                    </label>
                    <label className="flex items-start cursor-pointer select-none">
                      <input type="checkbox" checked={bookingData.agreePrivacy} onChange={(e) => setBookingData({ ...bookingData, agreePrivacy: e.target.checked })} className="rounded border-slate-300 text-[#0066cc] mt-1 mr-3 h-4 w-4 shrink-0" />
                      <span className="text-sm text-slate-600 font-medium">I consent to my data being processed in accordance with GDPR <span className="text-blue-600 hover:underline">Privacy Policy</span> *</span>
                    </label>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)} className="h-12 px-6 rounded-lg font-medium text-slate-700">← Back</Button>
                    <Button disabled={!isContactValid} onClick={() => setCurrentStep(currentStep + 1)} className="flex-1 h-12 text-sm font-semibold text-white bg-[#0066cc]">Review Booking</Button>
                  </div>
                </div>
              )}

              {/* STEP 6: REVIEW PRE-FLIGHT SUMMARY */}
              {currentStep === reviewStepIndex && (
                <div className="space-y-8 text-slate-800 text-left">
                  <div className="space-y-2">
                    <h1 className="text-3xl font-bold text-[#0d233a] tracking-tight">Review Your Booking</h1>
                    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-800 text-sm">
                      <div className="text-amber-500 font-bold shrink-0 text-base">⚠️</div>
                      <p><span className="font-semibold">Important:</span> Once submitted, changes cannot be made. Please review all your information carefully before confirming.</p>
                    </div>
                  </div>

                  <div className="border border-slate-100 shadow-sm rounded-2xl p-6 md:p-8 space-y-8 bg-white">
                    <div className="space-y-4">
                      <h3 className="text-lg font-bold text-[#0d233a] border-b border-slate-100 pb-2">Service Details</h3>
                      <div className="grid grid-cols-1 gap-y-3 text-sm">
                        <div className="flex justify-between items-center"><span className="text-slate-500">Location:</span><span className="font-medium">{bookingData.location || "—"}</span></div>
                        <div className="flex justify-between items-center"><span className="text-slate-500">Type:</span><span className="font-medium text-right">{bookingData.serviceType || "—"}</span></div>
                        <div className="flex justify-between items-center"><span className="text-slate-500">Property Size:</span><span className="font-medium text-right">{getPropertyLabel()}</span></div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-bold text-[#0d233a] border-b border-slate-100 pb-2">Schedule</h3>
                      <div className="grid grid-cols-1 gap-y-3 text-sm">
                        <div className="flex justify-between items-center"><span className="text-slate-500">Date:</span><span className="font-medium">{bookingData.preferredDate || "—"}</span></div>
                        <div className="flex justify-between items-center"><span className="text-slate-500">Time:</span><span className="font-medium">{bookingData.preferredTime || "—"}</span></div>
                        <div className="flex justify-between items-center"><span className="text-slate-500">Access:</span><span className="font-medium">{bookingData.accessMethod === "property" ? "Keys at the property" : "Keys at alternative address"}</span></div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-lg font-bold text-[#0d233a] border-b border-slate-100 pb-2">Pricing Breakdown</h3>
                      <div className="grid grid-cols-1 gap-y-3 text-sm">
                        <div className="flex justify-between items-center"><span>Property Base Fee:</span><span className="font-medium text-slate-700">£{propertyBaseTotal}</span></div>
                        {isRegularClean && <div className="flex justify-between items-center"><span>Hourly Maintenance ({bookingData.bookingHours} hrs):</span><span className="font-medium text-slate-700">£{hourlyRateTotal}</span></div>}
                        {!isRegularClean && bookingData.additionalRooms.length > 0 && <div className="flex justify-between items-center"><span>Add-on Rooms ({bookingData.additionalRooms.length}):</span><span className="font-medium text-slate-700">£{addOnsTotal}</span></div>}
                        <div className="flex justify-between items-center border-t border-slate-200 pt-4 mt-2">
                          <span className="text-base font-bold text-[#0d233a]">Total:</span>
                          <span className="text-2xl font-extrabold text-[#0066cc]">£{orderTotal}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Button variant="outline" onClick={() => setCurrentStep(currentStep - 1)} disabled={isSubmitting} className="h-12 px-6 rounded-lg font-medium text-slate-700">← Edit Booking</Button>
                    <Button onClick={handleFinalSubmit} disabled={isSubmitting} className="flex-1 h-12 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm">{isSubmitting ? "Processing..." : "Confirm & Submit"}</Button>
                  </div>
                </div>
              )}
            </div>

            {/* FLOATING REAL-TIME SIDEBAR SUMMARY */}
            {currentStep >= 2 && (
              <div className="w-full lg:w-80 bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden sticky top-6 text-left">
                <div className="bg-[#0066cc] text-white px-6 py-4 font-bold text-lg tracking-wide">Your Booking</div>
                <div className="p-6 space-y-4">
                  {isRegularClean ? (
                    <>
                      <div className="flex justify-between items-center text-sm font-medium text-slate-600"><span>Frequency Rate</span><span className="text-slate-900 font-semibold">£{bookingData.frequencyPricePerHour}/hr</span></div>
                      <div className="flex justify-between items-center text-sm font-medium text-slate-600 pt-1"><span>Hours Choice</span><span className="text-slate-900 font-semibold">{bookingData.bookingHours} Hours</span></div>
                      {bookingData.regularRoomOption && <div className="flex justify-between items-center text-sm font-medium text-slate-600 pt-1 border-t border-slate-100"><span>Base Property</span><span className="text-slate-900 font-semibold">£{propertyBaseTotal}</span></div>}
                    </>
                  ) : (
                    <div className="flex justify-between items-center text-sm font-medium text-slate-600 pt-1"><span>Base Property</span><span className="text-slate-900 font-semibold">£{propertyBaseTotal}</span></div>
                  )}
                  
                  {bookingData.additionalRooms.length > 0 && !isRegularClean && (
                    <div className="space-y-1.5 pt-2 border-t border-slate-100">
                      <span className="text-xs uppercase font-bold text-slate-400 tracking-wider">Add-on Rooms</span>
                      {bookingData.additionalRooms.map(room => (
                        <div key={room} className="flex justify-between items-center text-xs text-slate-600 font-medium pl-1"><span>• {room}</span><span>+£10</span></div>
                      ))}
                    </div>
                  )}

                  {bookingData.preferredDate && (
                    <div className="pt-3 border-t border-slate-100 text-xs text-slate-500 space-y-1">
                      <div>📅 <strong>Date:</strong> {bookingData.preferredDate}</div>
                      {bookingData.preferredTime && <div>⏰ <strong>Time:</strong> {bookingData.preferredTime}</div>}
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-4 border-t border-slate-200">
                    <span className="text-base font-bold text-[#0d233a]">Total</span>
                    <span className="text-2xl font-black text-[#0066cc]">£{orderTotal}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          
          /* ==========================================================
             SCREEN DISPLAY SUCCESS CONFIRMATION BADGE CARD
             ========================================================== */
          <div className="w-full max-w-2xl bg-white rounded-2xl border-2 border-emerald-500 p-8 md:p-12 shadow-lg text-center space-y-8 relative overflow-hidden">
            <div className="mx-auto w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-500 border border-emerald-100">
              <svg className="w-10 h-10 stroke-[2.5]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            </div>

            <div className="space-y-3">
              <h1 className="text-4xl font-extrabold text-[#0d233a] tracking-tight">Booking Confirmed!</h1>
              <p className="text-slate-500 text-lg font-medium">Your booking has been successfully submitted.</p>
            </div>

            <div className="bg-[#f1f5f9] rounded-xl p-8 border border-slate-100 space-y-2">
              <span className="text-sm font-semibold uppercase tracking-wider text-slate-400">Booking Reference</span>
              <div className="text-2xl md:text-3xl font-black text-[#0066cc] tracking-wide">{bookingReference}</div>
            </div>

            <p className="text-slate-600 font-medium text-base max-w-md mx-auto leading-relaxed">
              You will receive a confirmation email at{" "}
              <strong className="text-slate-900 font-bold break-all">{bookingData.email || "samuelstar2000abc@gmail.com"}</strong>{" "}
              shortly.
            </p>

            <div className="pt-4 border-t border-slate-100">
              <button 
                type="button" 
                onClick={() => window.print()} 
                className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold text-base rounded-xl shadow-sm transition-all flex items-center justify-center gap-2 tracking-wide"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Quote as PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}